import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { productImg } from "@/lib/img";
import { ProductDetails } from "@/components/product/ProductSections";
import { trackEvent } from "@/lib/analytics";
import { trackProductView } from "@/lib/attribution";
import Seo from "@/components/Seo";
import { ShopBuyModal } from "@/components/ShopBuyModal";

const SITE = "https://voluntoldhq.com";
const KEY = "speech_vault";
const BODY = [
  "Most of us didn\u2019t get into coaching with a speech in our back pocket. We got voluntold, or our kid needed a coach, and then the big night came and we had to find the words. The best coach your kid ever has probably won\u2019t be famous. It might be you.",
  "The Speech Vault gives you six full speeches for the moments a season turns on: Clean Slate, This Is Our Story, SNAP Back, One-Third/Two-Thirds, You Get One, and Built for the 92%. Each one comes with when to use it, how long it runs, delivery notes, and spaces for your own story. Read them, make them yours, then deliver.",
];

const ld = (p) => ({
  "@context": "https://schema.org", "@type": "Product", name: p.name, description: p.blurb, image: `${SITE}${p.image}`, sku: KEY,
  brand: { "@type": "Brand", name: "VoluntoldHQ" },
  offers: { "@type": "Offer", price: (p.amount / 100).toFixed(2), priceCurrency: "USD", availability: "https://schema.org/InStock", url: `${SITE}/speech-vault` },
});

export default function SpeechVault() {
  const [p, setP] = useState(null);
  const [all, setAll] = useState([]);
  const [buying, setBuying] = useState(false);
  const [img, setImg] = useState(true);

  useEffect(() => {
    window.scrollTo(0, 0);
    api.get("/products").then(({ data }) => { setAll(data || []); setP((data || []).find((x) => x.lookup_key === KEY) || null); }).catch(() => {});
  }, []);
  useEffect(() => {
    if (!p) return;
    trackProductView(api, KEY);
    trackEvent("view_item", { currency: "USD", value: p.amount / 100, items: [{ item_id: KEY, item_name: p.name }] });
  }, [p]);

  const price = p ? `$${p.amount / 100}` : "$19";
  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 pb-16" data-testid="speech-vault-page">
      {p && <Seo title={p.name} brand="VoluntoldHQ" description={p.blurb} path="/speech-vault" image={`${SITE}${p.image}`} type="product" jsonLd={ld(p)} />}
      <div className="grid lg:grid-cols-[1fr_1.1fr] gap-6 lg:gap-14 items-start">
        <div className="border-2 border-[#D3CFC0] bg-[#E7E3D9] aspect-[4/5] max-h-[260px] lg:max-h-none w-full flex items-center justify-center lg:sticky lg:top-24">
          {img ? <img {...productImg(p?.image || "/products/speech_vault.png", "(min-width: 1024px) 45vw, 100vw")} alt="Coach's Speech Vault cover" onError={() => setImg(false)}
            className="w-full h-full object-contain p-3 lg:p-6" data-testid="speech-vault-cover" />
            : <span className="font-head uppercase text-3xl text-[#1E1E1E] text-center px-6">Coach&apos;s Speech Vault</span>}
        </div>
        <div>
          <span className="kicker" style={{ color: "#DB9130" }}>Digital download · Coaches</span>
          <h1 className="mt-2 font-head font-semibold uppercase text-3xl sm:text-5xl lg:text-6xl text-[#1E1E1E] leading-[1.02]" data-testid="speech-vault-title">Coach&apos;s Speech Vault</h1>
          <p className="mt-4 text-base md:text-lg text-[#3A3A3A]" data-testid="speech-vault-subhead">Six speeches for the moments that matter most in a season, ready to deliver.</p>
          <div className="mt-6 flex flex-wrap items-center gap-5">
            <span className="font-head text-4xl text-[#1E1E1E]" data-testid="speech-vault-price">{price}</span>
            <button onClick={() => p && setBuying(true)} disabled={!p} data-testid="speech-vault-buy-btn"
              className="bg-[#E8A33D] text-[#1A1A1A] font-head uppercase tracking-wide text-sm px-8 py-3.5 hover:bg-[#D6922C] transition-colors disabled:opacity-60">
              Buy Now
            </button>
          </div>
          <p className="mt-3 text-xs text-[#6E6E6E]">Instant PDF download after checkout, plus a copy in your receipt email. Secure checkout via Stripe.</p>
          <div className="mt-6 pt-6 border-t-2 border-[#D3CFC0] space-y-4 text-[#3A3A3A] leading-relaxed max-w-xl">{BODY.map((t) => <p key={t.slice(0, 12)}>{t}</p>)}</div>
        </div>
      </div>
      {p && <ProductDetails p={p} products={all} />}
      {buying && p && <ShopBuyModal product={p} source="speech_vault_page" onClose={() => setBuying(false)} />}
    </div>
  );
}
